// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback } from 'react';

import { isNil } from 'helpers/isNotNil';
import { useBendCart } from 'modules/Shop/EvolveFabrication/BendCreator/Cart/useBendCart';
import type { ValidatedFormData } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/BendCreatorForm';
import { useBendCreatorFormContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorFormContext';
import { useBendCreatorValidationContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorValidationContext';
import { useBendBuilder } from 'modules/Shop/EvolveFabrication/BendCreator/State/useBendBuilder';

/**
 * Adds the bend being built to the bend list and selects it, so the model shows it, then resets the builder to a
 * fresh, empty bend.
 * `canAdd` is true once the form is valid and the bend has passed server validation.
 */
export const useAddToBendList = () => {
  const form = useBendCreatorFormContext();
  const { lastValidation, isValidating, resetLastValidation } = useBendCreatorValidationContext();
  const { addBend, previewBend } = useBendCart();
  const { resetBuilder, isAutoNamed } = useBendBuilder();

  const canAdd = form.isValid() && !isValidating && lastValidation?.isSuccess === true;

  const addToBendList = useCallback(() => {
    if (form.validate().hasErrors || isValidating || isNil(lastValidation) || !lastValidation.isSuccess) return;
    // Validation has run, so every required field is non-null.
    const values = form.getValues() as ValidatedFormData;
    const cartBendId = addBend({
      values,
      validation: { request: lastValidation.request, response: lastValidation.response },
      isAutoNamed,
      duplicatedFrom: null,
    });
    previewBend(cartBendId);
    form.reset();
    resetLastValidation();
    resetBuilder();
  }, [form, isValidating, lastValidation, addBend, isAutoNamed, previewBend, resetLastValidation, resetBuilder]);

  return { canAdd, addToBendList };
};
